/**
 * Out-of-band channel telling a copy/export caller that a markdown serializer
 * swallowed a throw and shipped its input back as its output.
 *
 * The serializers return a bare `string` at ~167 call sites, so widening their
 * return type to carry the flag is not viable; a module-scoped counter lets the
 * enclosing operation notice a degraded payload without any signature change.
 *
 * Deliberately dependency-free: the two serializer modules import this, and
 * pulling the logger/router graph in through them would break their consumers'
 * test mocks. Reporting lives in `reportSerializerFallback.ts`.
 */

/**
 * `sanitizeError` projects a non-axios error to `{}`, and a serializer throw is
 * never an axios error — without the constructor name the "why" dimension of
 * the log would always be empty. The name is a type identifier, never content.
 */
export const describeErrorName = (error: unknown): string =>
  error instanceof Error ? error.name : typeof error;

let fallbackCount = 0;

/** Called from every serializer catch that returns its input unconverted. */
export const recordSerializerFallback = (): void => {
  fallbackCount += 1;
};

/**
 * Starts watching for fallbacks. The returned predicate reports whether any
 * serializer fell back since the watch began — a counter rather than a reset
 * flag, so overlapping watches can't clobber one another. Attribution is
 * best-effort: a watch spanning an await can pick up a fallback from an
 * unrelated concurrent operation.
 */
export const beginSerializerFallbackWatch = (): (() => boolean) => {
  const startedAt = fallbackCount;
  return () => fallbackCount > startedAt;
};
